import React, { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';

interface LyricLine {
  start: number; // seconds
  end?: number;
  text: string;
  startTime?: number;
}

interface SyncedLyricsProps {
  currentSongId: string | null;
  currentTime: number;
  isPlaying: boolean;
  onSeek?: (time: number) => void;
}

// Streaming-platform style synced lyrics (Tidal/Spotify-like): a centered,
// auto-scrolling column with the active line highlighted in red. The active
// line is the last line whose start time is <= currentTime (LRC semantics).
const SyncedLyrics: React.FC<SyncedLyricsProps> = ({ currentSongId, currentTime, isPlaying, onSeek }) => {
  const [lines, setLines] = useState<LyricLine[]>([]);
  const [fetchedFor, setFetchedFor] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLParagraphElement>(null);

  // Fetch lyrics when the song changes
  useEffect(() => {
    if (!currentSongId) {
      setLines([]);
      setFetchedFor(null);
      return;
    }
    if (fetchedFor === currentSongId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/songs/${currentSongId}/subtitles`);
        const data = res.data;
        const parsed: LyricLine[] = data && Array.isArray(data.subtitles) ? data.subtitles : [];
        if (!cancelled) {
          setLines(parsed);
          setFetchedFor(currentSongId);
        }
      } catch {
        if (!cancelled) {
          setLines([]);
          setFetchedFor(currentSongId);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentSongId, fetchedFor]);

  // Determine the active line index
  const lineStart = (l: LyricLine) => l.start ?? l.startTime ?? 0;
  let activeIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (currentTime + 0.15 >= lineStart(lines[i])) activeIndex = i;
    else break;
  }

  // Auto-scroll the active line to the vertical center of the container
  useEffect(() => {
    const container = scrollRef.current;
    const el = activeRef.current;
    if (!container || !el) return;
    const target = el.offsetTop - container.clientHeight / 2 + el.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, [activeIndex, lines.length]);

  // Show once playback has started and the current song has lyrics
  const visible =
    lines.length > 0 &&
    fetchedFor === currentSongId &&
    (isPlaying || currentTime > 0.1);

  // While lyrics are showing, shift the page content up (via a body class)
  useEffect(() => {
    document.body.classList.toggle('lyrics-active', visible);
    return () => document.body.classList.remove('lyrics-active');
  }, [visible]);

  if (!visible) return null;

  const fade =
    'linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)';

  return (
    <div className="fixed inset-x-0 bottom-[80px] z-30 h-[300px] px-4 pointer-events-none max-[640px]:bottom-[120px]">
      {/* soft gradient backdrop (no box) for readability over the visualizer */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent" />
      <div
        ref={scrollRef}
        className="relative h-full max-w-xl mx-auto overflow-y-auto py-[150px] [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: 'none',
          maskImage: fade,
          WebkitMaskImage: fade,
        }}
      >
        {lines.map((line, i) => {
          const isActive = i === activeIndex;
          return (
            <p
              key={i}
              ref={isActive ? activeRef : null}
              onClick={() => onSeek && onSeek(lineStart(line))}
              className={`px-2 py-1.5 leading-snug select-none cursor-pointer pointer-events-auto whitespace-pre-wrap transition-colors duration-200 ${
                isActive
                  ? 'text-[#ff3b3b] font-bold text-lg sm:text-xl'
                  : 'text-[#b53636] text-sm sm:text-base hover:text-[#ff6b6b]'
              }`}
              style={{
                textShadow: isActive
                  ? '0 0 12px rgba(255,45,45,0.9), 0 0 3px rgba(255,45,45,0.7), 0 1px 4px rgba(0,0,0,1)'
                  : '0 1px 3px rgba(0,0,0,1), 0 0 8px rgba(0,0,0,0.85)',
              }}
            >
              <span className="opacity-50 mr-1.5">{isActive ? '>' : '\u00A0'}</span>
              {line.text}
              {isActive && <span className="terminal-cursor ml-1">▮</span>}
            </p>
          );
        })}
      </div>
    </div>
  );
};

export default React.memo(SyncedLyrics);
